import { DatePicker, Select, Table } from 'antd';
import { ColumnProps } from 'antd/es/table';
import { observer } from 'mobx-react';
import moment from 'moment';
import React from 'react';
import { CreateButton } from '../../components/action-buttons';
import { Button } from '../../components/button';
import { PageTopBar } from '../../components/page-top-bar';
import { PaginationBar } from '../../components/pagination/pagination-bar';
import { TableHeaderWithSubtitle } from '../../components/table-header-with-subtitle';
import {
    useApplicationSelectionStore,
    useContestStore,
} from '../../contexts/mobx';
import type {
    ContestListItem,
    ContestSearchParams,
    ContestState,
} from '../../stores/contest';
import { RegionTag } from '../../utils/games';
import { convertUtcToLocalMoment } from '../../utils/timezone';
import { useNavigation } from '../../utils/use-navigation';
import { useSearchParams } from '../../utils/use-search-params';
import { CONTEST_STATES, ContestStateTag } from './contest-state-tag';
import style from './style.module.scss';

const { RangePicker } = DatePicker;

const DAY_FORMAT = 'YYYY-MM-DD';

function inLanguage<T extends { lang: 'en' | 'fr' }>(
    items: T[],
    lang: 'en' | 'fr'
): T | undefined {
    return items.find((item) => item.lang === lang);
}

const renderState = (_: unknown, contest: ContestListItem) => (
    <ContestStateTag state={contest.state} />
);

const renderThumbnail = (_: unknown, contest: ContestListItem) => {
    const image =
        inLanguage(contest.images, 'en') ?? inLanguage(contest.images, 'fr');
    return <img src={image?.url ?? ''} alt="" className={style.thumbnail} />;
};

const renderTitle = (_: unknown, contest: ContestListItem) => (
    <>
        <div className={style.title}>
            {inLanguage(contest.translations, 'en')?.title ?? `#${contest.id}`}
        </div>
        <div className={style.secondary_title}>
            {inLanguage(contest.translations, 'fr')?.title}
        </div>
    </>
);

const renderRegion = (_: unknown, contest: ContestListItem) => (
    <RegionTag provinces={contest.provinces} />
);

/** A date and its time, in the contest's own time zone. */
function renderMoment(utc: string, timeZone: string) {
    const local = convertUtcToLocalMoment(utc, timeZone);
    return (
        <div className="text_center inline_block">
            <div style={{ marginBottom: '2px' }}>{local?.format('ll')}</div>
            <div className="lil_label">{local?.format('LT')}</div>
        </div>
    );
}

const renderDates = (_: unknown, contest: ContestListItem) => (
    <div className="dates_holder" style={{ padding: '4px 0' }}>
        {renderMoment(contest.startAt, contest.timeZone)}
        {renderMoment(contest.endAt, contest.timeZone)}
    </div>
);

const columns: ColumnProps<ContestListItem>[] = [
    {
        title: 'State',
        key: 'state',
        render: renderState,
        align: 'center',
        width: 104,
    },
    {
        title: 'Image',
        key: 'image',
        render: renderThumbnail,
        align: 'center',
        width: 106,
    },
    {
        title: 'Title',
        key: 'title',
        render: renderTitle,
    },
    {
        title: 'Region',
        key: 'region',
        render: renderRegion,
    },
    {
        title: (
            <TableHeaderWithSubtitle
                title="Dates"
                subtitle="Start date / End date"
            />
        ),
        key: 'dates',
        render: renderDates,
    },
    {
        title: 'Participants',
        key: 'participantCount',
        align: 'right',
        render: (_: unknown, contest: ContestListItem) =>
            contest.participantCount.toLocaleString('en-CA'),
    },
    {
        title: (
            <TableHeaderWithSubtitle
                title="Chances"
                subtitle="Weight in the draw"
            />
        ),
        key: 'chanceCount',
        align: 'right',
        render: (_: unknown, contest: ContestListItem) =>
            contest.chanceCount.toLocaleString('en-CA'),
    },
];

export const ContestsPage = observer(() => {
    const store = useContestStore();
    const navigation = useNavigation();
    const applicationSelectionStore = useApplicationSelectionStore();
    const { selectedApplication } = applicationSelectionStore;
    const search = useSearchParams<ContestSearchParams>();
    const { setParams } = search;

    React.useEffect(() => {
        store.fetchList(search.params);
    }, [search.params, selectedApplication, store]);

    // Another application lists other contests: start again from the first page.
    const previousApplication = React.useRef(selectedApplication);
    React.useEffect(() => {
        if (previousApplication.current !== selectedApplication) {
            previousApplication.current = selectedApplication;
            setParams({ page: 0 });
        }
    }, [selectedApplication, setParams]);

    const { from, to } = search.params;

    const clearFilters = () =>
        search.setParams({
            state: undefined,
            from: undefined,
            to: undefined,
            page: 0,
        });

    return (
        <>
            <PageTopBar title="Contests">
                <CreateButton onClick={() => navigation.goToContestCreate()}>
                    Create contest
                </CreateButton>
            </PageTopBar>

            <div className="action_zone_above_table">
                <div
                    className={`${style.selects_row} table_header_options_bar`}
                >
                    <Select<ContestState>
                        placeholder="State"
                        value={search.params.state}
                        onChange={(state) =>
                            search.setParams({ state, page: 0 })
                        }
                        allowClear
                    >
                        {Object.entries(CONTEST_STATES).map(
                            ([state, { label }]) => (
                                <Select.Option key={state} value={state}>
                                    {label}
                                </Select.Option>
                            )
                        )}
                    </Select>
                    <RangePicker
                        value={
                            from && to
                                ? [
                                      moment(from, DAY_FORMAT),
                                      moment(to, DAY_FORMAT),
                                  ]
                                : null
                        }
                        onChange={(dates) =>
                            search.setParams({
                                from: dates?.[0]?.format(DAY_FORMAT),
                                to: dates?.[1]?.format(DAY_FORMAT),
                                page: 0,
                            })
                        }
                    />
                    <Button
                        variant="secondary-destructive"
                        onClick={clearFilters}
                    >
                        Clear Filters
                    </Button>
                </div>
            </div>

            <Table<ContestListItem>
                className="clickable_table_body_rows"
                rowKey="id"
                dataSource={store.contests?.results}
                columns={columns}
                pagination={false}
                loading={store.isFetchingList}
                onRow={(contest) => ({
                    onClick: () => navigation.goToContestEdit(contest.id),
                })}
            />

            <div className={style.pagination}>
                <PaginationBar
                    pagination={{
                        page: search.params.page,
                        pageSize: search.params.perPage,
                        total: store.contests?.total,
                        onPageChange: (page) => search.setParams({ page }),
                        onPageSizeChange: (perPage) =>
                            search.setParams({ perPage, page: 0 }),
                    }}
                />
            </div>
        </>
    );
});
